import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BlockerFunction, useBlocker } from 'react-router-dom';

import type { PropsWithChildren } from 'react';

type UseNavigationGuardReturn = {
  allowNextNavigation: () => void;
  cancel: () => void;
  hasUnsavedChanges: boolean;
  isNavigationBlocked: boolean;
  proceed: () => void;
};

type NavigationGuardContextValue = UseNavigationGuardReturn & {
  register: (id: symbol, enabled: boolean) => void;
  unregister: (id: symbol) => void;
};

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

/**
 * Provides a single React Router blocker for the app subtree.
 *
 * Each useNavigationGuard call registers an instance in this provider.
 * Navigation is blocked when at least one registered instance is enabled.
 */
export function NavigationGuardProvider({ children }: PropsWithChildren) {
  const [enabledCount, setEnabledCount] = useState(0);
  const allowNextNavigationRef = useRef(false);
  const registeredGuardsRef = useRef(new Set<symbol>());

  const register = useCallback((id: symbol, enabled: boolean) => {
    const isRegistered = registeredGuardsRef.current.has(id);

    if (enabled && !isRegistered) {
      registeredGuardsRef.current.add(id);
      setEnabledCount(count => count + 1);
      return;
    }

    if (!enabled && isRegistered) {
      registeredGuardsRef.current.delete(id);
      setEnabledCount(count => count - 1);
    }
  }, []);

  const unregister = useCallback((id: symbol) => {
    if (registeredGuardsRef.current.has(id)) {
      registeredGuardsRef.current.delete(id);
      setEnabledCount(count => count - 1);
    }
  }, []);

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (currentLocation.pathname === nextLocation.pathname) {
        return false;
      }

      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }

      return enabledCount > 0;
    },
    [enabledCount]
  );
  const blocker = useBlocker(shouldBlock);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({
      allowNextNavigation,
      cancel: () => blocker.reset?.(),
      hasUnsavedChanges: enabledCount > 0,
      isNavigationBlocked: blocker.state === 'blocked',
      proceed: () => blocker.proceed?.(),
      register,
      unregister
    }),
    [allowNextNavigation, blocker, enabledCount, register, unregister]
  );

  return createElement(NavigationGuardContext.Provider, { value }, children);
}

/**
 * Registers the current component in the shared navigation guard.
 *
 * @param when Whether this instance should participate in blocking.
 * @returns Actions and state for the shared blocker.
 */
export function useNavigationGuard(when = true): UseNavigationGuardReturn {
  const context = useContext(NavigationGuardContext);

  if (!context) {
    throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
  }

  const instanceIdRef = useRef(Symbol('navigation-guard'));

  useEffect(() => {
    context.register(instanceIdRef.current, when);

    return () => {
      context.unregister(instanceIdRef.current);
    };
  }, [context, when]);

  return {
    allowNextNavigation: context.allowNextNavigation,
    cancel: context.cancel,
    hasUnsavedChanges: context.hasUnsavedChanges,
    isNavigationBlocked: context.isNavigationBlocked,
    proceed: context.proceed
  };
}
